import { translations } from '@aws-amplify/ui-react';
import { Device } from '@capacitor/device';
import { Network } from '@capacitor/network';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { IonApp, isPlatform } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { ThemeProvider, createTheme } from '@material-ui/core';
import { I18n } from 'aws-amplify';
import React, { useEffect, useState } from 'react';
import { IntlProvider, MessageFormatElement } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router';
import { AppAuthenticator } from './components/authenticator';
import { ConnectionStatusInfoModal } from './components/connection-lost-modal/ConnectionStatusInfoModal';
import { LogDetailsFormModal } from './components/job-metadata-form/LogDetailsFormModal';
import { darkModeTheme } from './global-styles/theme-provider-themes/darkModeTheme';
import { lightModeTheme } from './global-styles/theme-provider-themes/lightModeTheme';
import { TosPage } from './pages/TosPage';
import { LandingPage } from './pages/LandingPage';
import AppLayout from './pages/app-layout/AppLayout';
import AppSocketContext from './pages/app-layout/AppSocketContext';
import { commonActions } from './redux/common/actions';
import { selectAppTheme } from './redux/profile-preferences/selectors';
import { useResolvedTheme } from './theme/useResolvedTheme';
import { LogPrintPage } from './pages/job-info/LogPrintPage';
import { AppRouteConstants } from './pages/app-layout/AppRouteConstants';
import { LogoutPage } from './pages/logout/LogoutPage';
import { useIsMobile } from './utils/helpers/platformHelpers';
import { ConnectControllerModal } from './components/connect-controller-modal/ConnectControllerModal';
import { JobReportGuestLayout } from './pages/job-report-guest/JobReportGuestLayout';
import { JobReportGuestLandingPage } from './pages/job-report-guest/JobReportGuestLandingPage';
import { TosPageForGuest } from './pages/TosPageForGuest';

interface LocaleInfo {
  messages: Record<string, string> | Record<string, MessageFormatElement[]>;
  locale: string;
}

/**
 * A hook that enables/disables dark mode in CSS in response to the user's theme preference, for styling ionic components.
 * @returns The MUI theme object for the current theme.
 */
const useAppTheme = () => {
  const theme = useSelector(selectAppTheme);
  const resolvedTheme = useResolvedTheme();

  // An effect that keeps the user's theme preference saved in local storage
  useEffect(() => {
    window.localStorage.setItem('dq.theme', theme);
  }, [theme]);

  // An effect that sets the "dark" class on the body element when the preferred theme is dark.
  // This is how ionic detects dark mode.
  useEffect(() => {
    document.body.classList.toggle('dark', resolvedTheme === 'dark');
    // Intentionally 'hybrid' and not 'mobile', this next line can only be called on actual devices
    if (isPlatform('hybrid')) StatusBar.setStyle({ style: resolvedTheme === 'dark' ? Style.Dark : Style.Light });
  }, [resolvedTheme]);

  return React.useMemo(() => createTheme(resolvedTheme === 'dark' ? darkModeTheme : lightModeTheme), [resolvedTheme]);
};

/** A hook that sets up listeners for the device's current network state. */
const useNetworkStateListener = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    const init = async () => {
      const initialState = await Network.getStatus();
      dispatch(commonActions.networkStateChanged({ networkState: initialState.connectionType }));
    };
    init();

    const listener = Network.addListener('networkStatusChange', event => {
      dispatch(commonActions.networkStateChanged({ networkState: event.connectionType }));
    });

    return () => {
      listener.then(h => h.remove());
    };
  }, [dispatch]);
};

/** A hook that hides the native splash screen after the app has finished loading. */
const useSplashScreen = (localeLoaded: boolean) => {
  useEffect(() => {
    if (localeLoaded) SplashScreen.hide();
  }, [localeLoaded]);
};

const loadLocaleMessages = (locale: string): Promise<any> => {
  I18n.putVocabularies(translations);
  switch (locale) {
    case 'nl':
      return import('./intl/lang/compiled/nl.json');
    case 'en':
      return import('./intl/lang/compiled/en.json');
    default:
      return import('./intl/lang/compiled/en.json');
  }
};

/** A hook that handles loading the messages for the user's locale. */
const useLocaleMessages = (): LocaleInfo | undefined => {
  const [localeInfo, setLocaleInfo] = useState<LocaleInfo | undefined>();

  React.useEffect(() => {
    const setupLanguage = async () => {
      const languageCode = await Device.getLanguageCode();
      const regionTag = await Device.getLanguageTag();
      const messages = await loadLocaleMessages(languageCode.value);
      await I18n.setLanguage(languageCode.value);

      setLocaleInfo({
        messages: messages,
        locale: regionTag.value || languageCode.value,
      });
    };

    setupLanguage();
  }, []);

  return localeInfo;
};

const App: React.FC = () => {
  const appTheme = useAppTheme();
  const localeInfo = useLocaleMessages();
  const isMobile = useIsMobile();
  useNetworkStateListener();
  useSplashScreen(!!localeInfo);

  return (
    <IntlProvider locale={localeInfo?.locale ?? 'en-US'} defaultLocale="en-US" messages={localeInfo?.messages}>
      <ThemeProvider theme={appTheme}>
        <IonApp>
          {/**
           * Each time we switch "experiences", this forces the router to completely reset,
           * preventing some bugs with navigation in the scenario that we do change experiences
           * at runtime after the user has already navigated around some.
           */}
          <IonReactRouter key={isMobile.toString()}>
            <Switch>
              <Route exact path="/logout" component={LogoutPage} />
              <Route exact path="/landing" component={LandingPage} />
              <Route exact path="/tos-for-guest" component={TosPageForGuest} />
              <Route path="/job-report-share/:id" component={JobReportGuestLandingPage} />
              <Route path="/shared-job-reports/:id" component={JobReportGuestLayout} />
              <Redirect exact path="/" to="/landing" />
              <Route component={AuthenticatedApplication} />
            </Switch>
          </IonReactRouter>
        </IonApp>
      </ThemeProvider>
    </IntlProvider>
  );
};
export default App;

export const AuthenticatedApplication: React.FC = () => {
  return (
    <AppAuthenticator>
      <AppSocketContext>
        <Switch>
          <Route exact path="/tos" component={TosPage} />
          <Route exact path={AppRouteConstants.LogPrint} component={LogPrintPage} />
          <Route component={AppLayout} />
        </Switch>
        <ConnectControllerModal />
      </AppSocketContext>
      <ConnectionStatusInfoModal />
      <LogDetailsFormModal />
    </AppAuthenticator>
  );
};
